/**
 * Custom hook for counting milestone rows in the accounting grid
 * Excludes full-width group header rows from the count
 */

import { useState, useCallback, useMemo } from "react";
import { type GridRowData } from "../utils/transformForecastData";

import debounce from "helpers/debounce";

const debouncedUpdateRowCount = debounce(
  (api: any, setCount: (count: number) => void) => {
    if (!api) return;

    let milestoneCount = 0;

    // Iterate through all displayed rows after filtering
    api.forEachNodeAfterFilter((node: any) => {
      // Only count milestone rows, exclude full-width group headers
      if (node.data?.rowType === "milestone") {
        milestoneCount++;
      }
    });

    setCount(milestoneCount);
  },
  100,
);

export const useAccountingRowCount = (gridApi: any, rowData: GridRowData[]) => {
  const [filteredCount, setFilteredCount] = useState<number | null>(null);

  // Calculate initial count from row data when grid API isn't available yet
  const initialCount = useMemo(() => {
    if (!rowData) return 0;
    return rowData.filter((row) => row.rowType === "milestone").length;
  }, [rowData]);

  const updateRowCount = useCallback(() => {
    if (gridApi) {
      debouncedUpdateRowCount(gridApi, setFilteredCount);
    } else {
      // Fallback to counting from rowData when gridApi not ready
      setFilteredCount(initialCount);
    }
  }, [gridApi, initialCount]);

  return {
    filteredCount: filteredCount ?? initialCount,
    totalCount: filteredCount ?? initialCount,
    onFirstDataRendered: updateRowCount,
    onGridReady: updateRowCount,
    onRowDataUpdated: updateRowCount,
    onFilterChanged: updateRowCount,
  };
};
